import { CircularProgress } from "@mui/material";
import MDBox from "components/MDBox";
import { useCallback, useMemo, useState, ReactNode } from "react";
import JobsSchedules from "../JobsSchedules/JobsSchedules";
import JobsSchedulesCalendar from "../JobsSchedulesCalendar/JobsSchedulesCalendar";
import useGetAllCrews, { CustomCrews, CustomCrew } from "hooks/crews/useGetAllCrews";
import CalendarContext from "./CalendarContext";
import { omit } from "ramda";
import { v4 as uuidv4 } from "uuid";
import MDButton from "components/MDButton";
import useModalState from "hooks/useModalState";
import Modal from "modules/Modal/Modal";
import CreateUpdateCustomEvent from "modules/CustomEvent/CreateUpdateCustomEvent";
import useGetAllOrganizationUsers from "hooks/users/useGetAllOrganizationUsers";
import ScheduleFilters from "./ScheduleFilters";

export default function JobsScheduleDataLoader({
  jobId,
  children,
}: { jobId?: string | undefined; children?: ReactNode } = {}) {
  const { data: crews, loading, error } = useGetAllCrews();

  if (loading) {
    return <CircularProgress color="inherit" />;
  }

  return (
    <>
      {crews ? (
        <JobsSchedule jobId={jobId} crews={crews}>
          {children}
        </JobsSchedule>
      ) : null}
    </>
  );
}

function JobsSchedule({
  jobId,
  crews,
  children,
}: {
  jobId?: string | undefined;
  crews: CustomCrews;
  children?: ReactNode;
}) {
  const [checked, setChecked] = useState<
    Record<string, (CustomCrew & { checked: boolean }) | undefined>
  >(() => {
    return crews.reduce((acc, val) => {
      return {
        ...acc,
        [val.id]: {
          ...val,
          checked: true,
        },
      };
    }, {});
  });
  const [showCustom, setShowCustom] = useState<boolean>(true);
  const { open, onClose, onOpen } = useModalState();

  const [calendarsToUpdate, setCalendarsToUpdate] = useState({});

  const [selectedProjectManagers, setSelectedProjectManagers] = useState<string[]>([]);
  const { data: organizationUsers, loading: loadingUsers } = useGetAllOrganizationUsers();

  const projectManagerOptions = useMemo(() => {
    if (!organizationUsers) return [];
    return organizationUsers.map((user) => ({
      label: `${user.firstName} ${user.lastName}`,
      value: user.id,
    }));
  }, [organizationUsers]);

  const subscribeCalendarToUpdates = useCallback(
    (callback: () => void) => {
      const id = uuidv4();
      setCalendarsToUpdate((prevState) => ({
        ...prevState,
        [id]: callback,
      }));
      return id;
    },
    [setCalendarsToUpdate]
  );

  const triggerCalendarUpdate = useCallback(() => {
    Object.values(calendarsToUpdate).forEach((callback: any) => {
      if (typeof callback === "function") {
        callback();
      }
    });
  }, [calendarsToUpdate]);

  const unsubscribeCalendarFromUpdates = useCallback(
    (key: string) => {
      setCalendarsToUpdate((prevState) => omit([key])(prevState));
    },
    [setCalendarsToUpdate]
  );

  const handleCrewCheckedChanged = useCallback(
    (crew, checked) => {
      setChecked((prevValues) => ({
        ...prevValues,
        [crew.value]: {
          ...prevValues[crew.value],
          checked,
        },
      }));
    },
    [setChecked]
  );

  const handleToggleAllCrews = useCallback(
    (crews, checked) => {
      crews.map((crew) => {
        setChecked((prevValues) => ({
          ...prevValues,
          [crew.value]: {
            ...prevValues[crew.value],
            checked,
          },
        }));
      });
    },
    [setChecked]
  );

  const onScheduleUpdates = useCallback(() => {
    Object.keys(calendarsToUpdate).forEach((key) => {
      calendarsToUpdate[key]?.(); //call every callback
    });
  }, [calendarsToUpdate]);

  const crewOptions = useMemo(() => {
    if (!crews) {
      return [];
    }

    return crews.map((crew) => {
      return {
        label: crew.name,
        value: crew.id,
      };
    });
  }, [crews]);

  const handleCrewFilterChange = useCallback(
    (selectedValues: string[]) => {
      crewOptions.forEach((crew) => {
        handleCrewCheckedChanged(crew, selectedValues.includes(crew.value));
      });
    },
    [crewOptions, handleCrewCheckedChanged]
  );

  const handleProjectManagerFilterChange = useCallback((selectedValues: string[]) => {
    setSelectedProjectManagers(selectedValues);
  }, []);

  return (
    <CalendarContext.Provider
      value={{ subscribeCalendarToUpdates, unsubscribeCalendarFromUpdates, triggerCalendarUpdate }}
    >
      <MDBox
        display="flex"
        sx={{
          justifyContent: "space-between",
        }}
      >
        <MDBox display="flex" gap={2}>
          {/* <MDButton color="blue" onClick={() => handleToggleAllCrews(crewOptions, true)}>
            Select All
          </MDButton>
          <MDButton color="blue" onClick={() => handleToggleAllCrews(crewOptions, false)}>
            Deselect All
          </MDButton> */}
        </MDBox>
      </MDBox>
      <MDBox display="flex" mb={3} mt={2} justifyContent="space-between" alignItems="center">
        <ScheduleFilters
          checked={checked}
          crewOptions={crewOptions}
          projectManagerOptions={projectManagerOptions}
          selectedProjectManagers={selectedProjectManagers}
          onCrewChange={handleCrewFilterChange}
          onProjectManagerChange={handleProjectManagerFilterChange}
        />
        <MDBox display="flex" gap={2}>
          <MDButton color="blue" onClick={() => setShowCustom(!showCustom)}>
            {showCustom ? "Hide Custom Events" : "Show All Events"}
          </MDButton>
          <MDButton color="dark" onClick={onOpen}>
            Add Event
          </MDButton>
        </MDBox>
      </MDBox>
      <JobsSchedulesCalendar
        showCustomEvents={showCustom}
        jobIds={jobId ? [jobId] : null}
        crewIds={Object.keys(checked).filter((key) => checked[key].checked)}
        projectManagerIds={selectedProjectManagers}
      />
      {children}
      <MDBox my={3}>
        <JobsSchedules jobIds={jobId ? [jobId] : null} onSchedulesUpdate={onScheduleUpdates} />
      </MDBox>
      <Modal open={open} onClose={onClose}>
        <CreateUpdateCustomEvent action="create" onClose={onClose} onSuccess={onScheduleUpdates} />
      </Modal>
    </CalendarContext.Provider>
  );
}
